import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from 'src/common/utils/api/fetchWrapper';

type SignatureDownloadUrlResponse = {
  downloadUrl: string | null;
  hasSignature: boolean;
};

type UploadUrlResponse = {
  uploadUrl: string;
  location: string;
};

export const useSignatureDownloadUrl = (hasSignature: boolean) => {
  const { data, isLoading, refetch } = useQuery<SignatureDownloadUrlResponse>({
    queryKey: ['me', 'signature'],
    enabled: hasSignature,
  });

  return {
    downloadUrl: data?.downloadUrl ?? null,
    hasSignature: data?.hasSignature ?? false,
    isLoading,
    refetch,
  };
};

export const useUploadSignature = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<void, Error, File>({
    mutationFn: async (file: File) => {
      const uploadUrlResponse = await fetch(`${API_URL}/me/signature/upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: file.type }),
      });

      if (!uploadUrlResponse.ok) {
        throw new Error(`Failed to get upload URL: ${uploadUrlResponse.statusText}`);
      }

      const { uploadUrl, location } = (await uploadUrlResponse.json()) as UploadUrlResponse;

      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!s3Response.ok) {
        throw new Error(`Failed to upload to S3: ${s3Response.statusText}`);
      }

      const confirmResponse = await fetch(`${API_URL}/me/signature/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location }),
      });

      if (!confirmResponse.ok) {
        throw new Error(`Failed to confirm upload: ${confirmResponse.statusText}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'signature'] });
    },
  });

  return {
    uploadSignature: mutateAsync,
    isLoading,
  };
};

export const useDeleteSignature = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<void, Error, void>({
    mutationFn: async () => {
      const response = await fetch(`${API_URL}/me/signature`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'signature'] });
    },
  });

  return {
    deleteSignature: mutateAsync,
    isLoading,
  };
};
